import { useEffect, useMemo, useState } from "react";
import { Button } from "@component/button";
import { Card } from "@component/card";
import { Input } from "@component/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { Job, JobApplication, JobWorkAuthorizationMetadata, SurveyFormData } from "@type/job";
import { availabilityOptions, employmentStatusOptions, getWorkAuthorizationOptions } from "@constant/app";
import { LocationMetadata } from "@service/employer";
import { applyForJob } from "@service/job";
import { trackEvent } from "@util/mixpanel";
import { cn } from "@util/tailwind";

interface Props {
    job?: Job;
    onComplete?: (jobApplication: JobApplication) => void;
    is_view?: boolean;
    is_show_button?: boolean;
    /** Drop the max-w-4xl reading-width cap so the survey fills its container.
     *  Used by the HR full-page views; the candidate apply form keeps the cap. */
    is_full_width?: boolean;
    onButtonClick?: () => void;
    work_authorization_metadata?: JobWorkAuthorizationMetadata;
    elligility_answers?: SurveyFormData;
    location_metadata?: LocationMetadata;
}

const COUNTRY_CURRENCY_MAP: Record<string, string> = {
    "Malaysia": "MYR",
    "United States": "USD",
    "Singapore": "SGD",
    "Indonesia": "IDR",
};

function getCurrencyByCountry(country?: string): string {
    if (!country) return "USD";
    return COUNTRY_CURRENCY_MAP[country] || "USD";
}

export default function SWEExperienceSurvey(props: Props) {
    const locationMetadata = props.is_view ? props.location_metadata : props?.job?.location_metadata;
    const defaultCurrency = getCurrencyByCountry(locationMetadata?.country);
    const [formData, setFormData] = useState<SurveyFormData>(
        props.is_view && props.elligility_answers
            ? props.elligility_answers
            : {
                employmentStatus: "",
                employmentStatusOther: "",
                expectedSalary: "",
                expectedSalaryCurrency: defaultCurrency,
                startAvailability: "",
                currentSalary: "",
                currentSalaryCurrency: defaultCurrency,
                relocationWillingness: "",
                shiftWillingness: "",
                workAuthorizationStatus: "",
                educationLevel: "",
            }
    );

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [touched, setTouched] = useState<Partial<Record<keyof SurveyFormData, boolean>>>({});

    useEffect(() => {
        if (props.is_view) return;
        if (props.job && !props.job?.is_enable_work_authorization) {
            setIsSubmitting(true);

            // Call the applyForJob service with the job ID
            applyForJob({
                job_id: props.job?.id || "",
                referral_code: sessionStorage.getItem("__referral_code__") || undefined,
            }).then((response) => {
                if (response) {
                    trackEvent("job_application_submitted", {
                        job_id: props.job?.id || "",
                        job_title: props.job?.job_title || "",
                        company_name: props.job?.company_name || "",
                        job_referral_code: sessionStorage.getItem("__referral_code__") || "",
                        enabled_work_authorization: props.job?.is_enable_work_authorization,
                    });
                    props.onComplete?.(response);
                }
            }).catch((error) => {
                console.error("Error applying for job:", error);
            }).finally(() => {
                setIsSubmitting(false);
            })
        }
    }, [props.job])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isFormValid()) {
            setIsSubmitting(true);

            try {
                // Call the applyForJob service with the job ID
                const response = await applyForJob({
                    job_id: props.job?.id || "",
                    referral_code: sessionStorage.getItem("__referral_code__") || undefined,
                    elligibility_answers: formData,
                });

                if (response) {
                    trackEvent("job_application_submitted", {
                        job_id: props.job?.id || "",
                        job_title: props.job?.job_title || "",
                        company_name: props.job?.company_name || "",
                        job_referral_code: sessionStorage.getItem("__referral_code__") || "",
                        elligibility_answers: formData,
                    });
                    props.onComplete?.(response);
                    setIsSubmitted(true);
                }
            } catch (error) {
                console.error("Error applying for job:", error);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const metadata = props.is_view ? props.work_authorization_metadata : props?.job?.work_authorization_metadata;

    const visibleQuestions = useMemo(() => {
        const questions: string[] = [];
        if (metadata?.collect_employment_status) questions.push("employmentStatus");
        if (metadata?.collect_expected_salary) questions.push("expectedSalary");
        if (metadata?.collect_start_availability) questions.push("startAvailability");
        if (metadata?.collect_current_salary) questions.push("currentSalary");
        if (metadata?.collect_relocation_willingness) questions.push("relocationWillingness");
        if (metadata?.collect_weekend_shift_willingness) questions.push("shiftWillingness");
        if (metadata?.collect_education_level) questions.push("educationLevel");
        if (metadata?.collect_work_authorization) questions.push("workAuthorizationStatus");
        return questions;
    }, [metadata]);

    let questionCounter = 0;
    const getNextNumber = () => ++questionCounter;

    const markTouched = (field: keyof SurveyFormData) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
    };

    const handleSelectChange = (field: keyof SurveyFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        markTouched(field);
    };

    const handleInputChange = (field: keyof SurveyFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleInputBlur = (field: keyof SurveyFormData) => {
        markTouched(field);
    };

    const handleSalaryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (["e", "E", "+", "-", "."].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleSalaryChange = (field: keyof SurveyFormData, value: string) => {
        const numericOnly = value.replace(/[^0-9]/g, "");
        handleInputChange(field, numericOnly);
    };

    const getFieldError = (field: string): string | null => {
        switch (field) {
            case "employmentStatus":
                if (formData.employmentStatus === "") return "Please select your employment status.";
                if (formData.employmentStatus === "other" && formData.employmentStatusOther.trim().length < 2)
                    return "Please specify your employment status (min. 2 characters).";
                return null;
            case "expectedSalary": {
                if (formData.expectedSalary.trim() === "") return "Please enter your expected salary.";
                const val = Number(formData.expectedSalary);
                if (isNaN(val) || val <= 0) return "Salary must be a positive number.";
                return null;
            }
            case "startAvailability":
                if (formData.startAvailability === "") return "Please select your availability.";
                return null;
            case "currentSalary": {
                if (formData.currentSalary.trim() === "") return "Please enter your current or last drawn salary.";
                const val = Number(formData.currentSalary);
                if (isNaN(val) || val <= 0) return "Salary must be a positive number.";
                return null;
            }
            case "relocationWillingness":
                if (formData.relocationWillingness === "") return "Please select an option.";
                return null;
            case "shiftWillingness":
                if (formData.shiftWillingness === "") return "Please select an option.";
                return null;
            case "workAuthorizationStatus":
                if (formData.workAuthorizationStatus === "") return "Please select an option.";
                return null;
            case "educationLevel":
                if (!formData.educationLevel) return "Please select your highest education level.";
                return null;
            default:
                return null;
        }
    };

    const isFormValid = () => {
        return visibleQuestions.every((q) => getFieldError(q) === null);
    };

    const handleSubmitWithValidation = (e: React.FormEvent) => {
        const allTouched: Partial<Record<keyof SurveyFormData, boolean>> = {};
        for (const q of visibleQuestions) {
            allTouched[q as keyof SurveyFormData] = true;
            if (q === "employmentStatus") allTouched.employmentStatusOther = true;
        }
        setTouched((prev) => ({ ...prev, ...allTouched }));
        handleSubmit(e);
    };

    if (!props.is_view && !props.job?.is_enable_work_authorization) {
        return null;
    }

    if (isSubmitted) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-950 flex items-center justify-center p-4">
                <div className="max-w-md text-center">
                    <div className="mb-6">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563eb] mx-auto"></div>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        Survey Submitted
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        Proceeding to next step...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={cn("w-full", !props.is_full_width && "max-w-4xl")}>
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                    <h1 className={cn("font-bold text-gray-900 dark:text-white", {
                        "text-xl sm:text-3xl": !props.is_view,
                        "text-xl": props.is_view,
                    })}>
                        Eligibility Check
                    </h1>
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 whitespace-nowrap">
                        {visibleQuestions.length} {visibleQuestions.length === 1 ? "Question" : "Questions"}
                    </div>
                </div>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    Please provide the following information to help us better understand your employment status and expectations.
                </p>
            </div>

            <Card className="py-6 px-4 sm:py-8 sm:px-8">
                <div className="space-y-8">
                    {/* Question: Employment Status */}
                    {metadata?.collect_employment_status && (
                        <div
                            className="space-y-4"
                            style={{ paddingTop: "8px", paddingBottom: "8px" }}
                        >
                            <div className="flex items-start gap-4">
                                <div
                                    className="flex-shrink-0 w-6 h-6 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center"
                                    style={{
                                        border: "0px solid rgb(229, 231, 235)",
                                        backgroundColor: "#dbeafe",
                                        color: "#1e40af",
                                    }}
                                >
                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                        {getNextNumber()}
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <label className="text-base font-medium text-gray-900 dark:text-white">
                                        What is your current employment status? <span className="text-red-500">*</span>
                                    </label>
                                </div>
                            </div>
                            <div className="ml-10 space-y-3">
                                <Select
                                    value={formData.employmentStatus}
                                    disabled={props.is_view}
                                    onValueChange={(value) =>
                                        handleSelectChange("employmentStatus", value)
                                    }
                                >
                                    <SelectTrigger
                                        className="w-full"
                                        style={{
                                            color: formData.employmentStatus
                                                ? undefined
                                                : "#71717a",
                                        }}
                                    >
                                        <SelectValue placeholder="Select an option" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(employmentStatusOptions).map(([value, label]) => (
                                            <SelectItem key={value} value={value}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {formData.employmentStatus === "other" && (
                                    <Input
                                        type="text"
                                        value={formData.employmentStatusOther}
                                        onChange={(e) =>
                                            handleInputChange("employmentStatusOther", e.target.value)
                                        }
                                        onBlur={() => handleInputBlur("employmentStatusOther")}
                                        placeholder="Please specify your employment status"
                                        className="w-full"
                                        disabled={props.is_view}
                                    />
                                )}
                                {touched.employmentStatus && getFieldError("employmentStatus") && (
                                    <p className="text-sm text-red-500">{getFieldError("employmentStatus")}</p>
                                )}
                            </div>
                        </div>
                    )}
                    {/* Question: Expected Salary */}
                    {metadata?.collect_expected_salary && (
                        <div
                            className="space-y-4"
                            style={{ paddingTop: "8px", paddingBottom: "8px" }}
                        >
                            <div className="flex items-start gap-4">
                                <div
                                    className="flex-shrink-0 w-6 h-6 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center"
                                    style={{
                                        border: "0px solid rgb(229, 231, 235)",
                                        backgroundColor: "#dbeafe",
                                        color: "#1e40af",
                                    }}
                                >
                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                        {getNextNumber()}
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <label className="text-base font-medium text-gray-900 dark:text-white">
                                        What is your expected monthly salary for this role? <span className="text-red-500">*</span>
                                    </label>
                                </div>
                            </div>
                            <div className="ml-10">
                                <div className="flex gap-2">
                                    <Select
                                        value={formData.expectedSalaryCurrency}
                                        onValueChange={(value) =>
                                            handleSelectChange("expectedSalaryCurrency", value)
                                        }
                                        disabled={props.is_view}
                                    >
                                        <SelectTrigger className="w-32">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="MYR">MYR</SelectItem>
                                            <SelectItem value="USD">USD</SelectItem>
                                            <SelectItem value="SGD">SGD</SelectItem>
                                            <SelectItem value="IDR">IDR</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={formData.expectedSalary}
                                        onChange={(e) =>
                                            handleSalaryChange("expectedSalary", e.target.value)
                                        }
                                        onKeyDown={handleSalaryKeyDown}
                                        onBlur={() => handleInputBlur("expectedSalary")}
                                        placeholder="Enter amount"
                                        className="flex-1"
                                        disabled={props.is_view}
                                    />
                                </div>
                                {touched.expectedSalary && getFieldError("expectedSalary") && (
                                    <p className="text-sm text-red-500 mt-1">{getFieldError("expectedSalary")}</p>
                                )}
                            </div>
                        </div>
                    )}
                    {/* Question: Start Availability */}
                    {metadata?.collect_start_availability && (
                        <div
                            className="space-y-4"
                            style={{ paddingTop: "8px", paddingBottom: "8px" }}
                        >
                            <div className="flex items-start gap-4">
                                <div
                                    className="flex-shrink-0 w-6 h-6 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center"
                                    style={{
                                        border: "0px solid rgb(229, 231, 235)",
                                        backgroundColor: "#dbeafe",
                                        color: "#1e40af",
                                    }}
                                >
                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                        {getNextNumber()}
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <label className="text-base font-medium text-gray-900 dark:text-white">
                                        When would you be available to start if selected? <span className="text-red-500">*</span>
                                    </label>
                                </div>
                            </div>
                            <div className="ml-10">
                                <Select
                                    value={formData.startAvailability}
                                    disabled={props.is_view}
                                    onValueChange={(value) =>
                                        handleSelectChange("startAvailability", value)
                                    }
                                >
                                    <SelectTrigger
                                        className="w-full"
                                        style={{
                                            color: formData.startAvailability
                                                ? undefined
                                                : "#71717a",
                                        }}
                                    >
                                        <SelectValue placeholder="Select an option" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {availabilityOptions.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                                    Select the earliest you could begin this role.
                                </p>
                                {touched.startAvailability && getFieldError("startAvailability") && (
                                    <p className="text-sm text-red-500 mt-1">{getFieldError("startAvailability")}</p>
                                )}
                            </div>
                        </div>
                    )}
                    {/* Question: Current or Last Drawn Salary */}
                    {metadata?.collect_current_salary && (
                        <div
                            className="space-y-4"
                            style={{ paddingTop: "8px", paddingBottom: "8px" }}
                        >
                            <div className="flex items-start gap-4">
                                <div
                                    className="flex-shrink-0 w-6 h-6 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center"
                                    style={{
                                        border: "0px solid rgb(229, 231, 235)",
                                        backgroundColor: "#dbeafe",
                                        color: "#1e40af",
                                    }}
                                >
                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                        {getNextNumber()}
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <label className="text-base font-medium text-gray-900 dark:text-white">
                                        What is your current or last drawn monthly salary? <span className="text-red-500">*</span>
                                    </label>
                                </div>
                            </div>
                            <div className="ml-10">
                                <div className="flex gap-2">
                                    <Select
                                        value={formData.currentSalaryCurrency}
                                        onValueChange={(value) =>
                                            handleSelectChange("currentSalaryCurrency", value)
                                        }
                                        disabled={props.is_view}
                                    >
                                        <SelectTrigger className="w-32">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="MYR">MYR</SelectItem>
                                            <SelectItem value="USD">USD</SelectItem>
                                            <SelectItem value="SGD">SGD</SelectItem>
                                            <SelectItem value="VND">VND</SelectItem>
                                            <SelectItem value="IDR">IDR</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={formData.currentSalary}
                                        onChange={(e) =>
                                            handleSalaryChange("currentSalary", e.target.value)
                                        }
                                        onKeyDown={handleSalaryKeyDown}
                                        onBlur={() => handleInputBlur("currentSalary")}
                                        placeholder="Enter amount"
                                        disabled={props.is_view}
                                        className="flex-1"
                                    />
                                </div>
                                {touched.currentSalary && getFieldError("currentSalary") && (
                                    <p className="text-sm text-red-500 mt-1">{getFieldError("currentSalary")}</p>
                                )}
                            </div>
                        </div>
                    )}
                    {/* Question: Relocation Willingness */}
                    {metadata?.collect_relocation_willingness && (
                        <div
                            className="space-y-4"
                            style={{ paddingTop: "8px", paddingBottom: "8px" }}
                        >
                            <div className="flex items-start gap-4">
                                <div
                                    className="flex-shrink-0 w-6 h-6 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center"
                                    style={{
                                        border: "0px solid rgb(229, 231, 235)",
                                        backgroundColor: "#dbeafe",
                                        color: "#1e40af",
                                    }}
                                >
                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                        {getNextNumber()}
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <label className="text-base font-medium text-gray-900 dark:text-white">
                                        This role is based in <b>{locationMetadata?.country}</b>. Would you be willing to relocate if selected? <span className="text-red-500">*</span>
                                    </label>
                                </div>
                            </div>
                            <div className="ml-10">
                                <Select
                                    value={formData.relocationWillingness}
                                    disabled={props.is_view}
                                    onValueChange={(value) =>
                                        handleSelectChange("relocationWillingness", value)
                                    }
                                >
                                    <SelectTrigger
                                        className="w-full"
                                        style={{
                                            color: formData.relocationWillingness
                                                ? undefined
                                                : "#71717a",
                                        }}
                                    >
                                        <SelectValue placeholder="Select an option" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="yes">Yes</SelectItem>
                                        <SelectItem value="no">No</SelectItem>
                                        <SelectItem value="open-to-discussion">Open to discussion</SelectItem>
                                    </SelectContent>
                                </Select>
                                {touched.relocationWillingness && getFieldError("relocationWillingness") && (
                                    <p className="text-sm text-red-500 mt-1">{getFieldError("relocationWillingness")}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Question: Weekend / Shift Willingness */}
                    {metadata?.collect_weekend_shift_willingness && (
                        <div
                            className="space-y-4"
                            style={{ paddingTop: "8px", paddingBottom: "8px" }}
                        >
                            <div className="flex items-start gap-4">
                                <div
                                    className="flex-shrink-0 w-6 h-6 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center"
                                    style={{
                                        border: "0px solid rgb(229, 231, 235)",
                                        backgroundColor: "#dbeafe",
                                        color: "#1e40af",
                                    }}
                                >
                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                        {getNextNumber()}
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <label className="text-base font-medium text-gray-900 dark:text-white">
                                        Are you willing to work weekends, shifts or overtime if required? <span className="text-red-500">*</span>
                                    </label>
                                </div>
                            </div>
                            <div className="ml-10">
                                <Select
                                    value={formData.shiftWillingness}
                                    disabled={props.is_view}
                                    onValueChange={(value) =>
                                        handleSelectChange("shiftWillingness", value)
                                    }
                                >
                                    <SelectTrigger
                                        className="w-full"
                                        style={{
                                            color: formData.shiftWillingness
                                                ? undefined
                                                : "#71717a",
                                        }}
                                    >
                                        <SelectValue placeholder="Select an option" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="yes">Yes</SelectItem>
                                        <SelectItem value="no">No</SelectItem>
                                        <SelectItem value="open-to-discussion">Open to discussion</SelectItem>
                                    </SelectContent>
                                </Select>
                                {touched.shiftWillingness && getFieldError("shiftWillingness") && (
                                    <p className="text-sm text-red-500 mt-1">{getFieldError("shiftWillingness")}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Question: Work Authorization Status */}
                    {/* Question: Highest education level (#3085) — submits the
                        normalized ladder KEY so the BE knockout comparison
                        (e.g. lt "diploma") ranks correctly. */}
                    {metadata?.collect_education_level && (
                        <div
                            className="space-y-4"
                            style={{ paddingTop: "8px", paddingBottom: "8px" }}
                        >
                            <div className="flex items-start gap-4">
                                <div
                                    className="flex-shrink-0 w-6 h-6 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center"
                                    style={{
                                        border: "0px solid rgb(229, 231, 235)",
                                        backgroundColor: "#dbeafe",
                                        color: "#1e40af",
                                    }}
                                >
                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                        {getNextNumber()}
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <label className="text-base font-medium text-gray-900 dark:text-white">
                                        What is your highest education level? <span className="text-red-500">*</span>
                                    </label>
                                </div>
                            </div>
                            <div className="ml-10">
                                <Select
                                    value={formData.educationLevel || ""}
                                    disabled={props.is_view}
                                    onValueChange={(value) =>
                                        handleSelectChange("educationLevel", value)
                                    }
                                >
                                    <SelectTrigger
                                        className="w-full"
                                        style={{
                                            color: formData.educationLevel
                                                ? undefined
                                                : "#71717a",
                                        }}
                                    >
                                        <SelectValue placeholder="Select your highest education level" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="below_secondary">Below secondary</SelectItem>
                                        <SelectItem value="secondary">Secondary (SPM · GCSE / O-Levels)</SelectItem>
                                        <SelectItem value="post_secondary">Post-secondary (STPM · A-Levels)</SelectItem>
                                        <SelectItem value="diploma">Diploma</SelectItem>
                                        <SelectItem value="bachelor">Bachelor's degree</SelectItem>
                                        <SelectItem value="postgraduate">Postgraduate</SelectItem>
                                    </SelectContent>
                                </Select>
                                {touched.educationLevel && getFieldError("educationLevel") && (
                                    <p className="text-sm text-red-500 mt-1">{getFieldError("educationLevel")}</p>
                                )}
                            </div>
                        </div>
                    )}

                    {metadata?.collect_work_authorization && (
                        <div
                            className="space-y-4"
                            style={{ paddingTop: "8px", paddingBottom: "8px" }}
                        >
                            <div className="flex items-start gap-4">
                                <div
                                    className="flex-shrink-0 w-6 h-6 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center"
                                    style={{
                                        border: "0px solid rgb(229, 231, 235)",
                                        backgroundColor: "#dbeafe",
                                        color: "#1e40af",
                                    }}
                                >
                                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                        {getNextNumber()}
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <label className="text-base font-medium text-gray-900 dark:text-white">
                                        Do you have the right to work in <b>{locationMetadata?.country}</b>? <span className="text-red-500">*</span>
                                    </label>
                                </div>
                            </div>
                            <div className="ml-10">
                                <Select
                                    value={formData.workAuthorizationStatus}
                                    disabled={props.is_view}
                                    onValueChange={(value) =>
                                        handleSelectChange("workAuthorizationStatus", value)
                                    }
                                >
                                    <SelectTrigger
                                        className="w-full"
                                        style={{
                                            color: formData.workAuthorizationStatus
                                                ? undefined
                                                : "#71717a",
                                        }}
                                    >
                                        <SelectValue placeholder="Select an option" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {getWorkAuthorizationOptions(locationMetadata?.country || "").map((option) => (
                                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {touched.workAuthorizationStatus && getFieldError("workAuthorizationStatus") && (
                                    <p className="text-sm text-red-500 mt-1">{getFieldError("workAuthorizationStatus")}</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {!props.is_view && (
                    <>
                        <hr className="mt-8 border-t border-gray-200 dark:border-gray-700" />

                        <div className="mt-8 space-y-4">
                            <p
                                className="text-sm text-gray-600 dark:text-gray-400"
                                style={{ color: "#71717a" }}
                            >
                                Please review your responses carefully as they cannot be changed once submitted.
                            </p>

                            <div className="flex justify-end">
                                <Button
                                    onClick={handleSubmitWithValidation}
                                    disabled={!isFormValid() || isSubmitting}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{
                                        paddingRight: "24px",
                                        paddingLeft: "24px",
                                        borderRadius: "0.375rem",
                                    }}
                                >
                                    {isSubmitting ? "Submitting..." : "Submit"}
                                </Button>
                            </div>
                        </div>
                    </>
                )}

                {props.is_view && props.is_show_button && (
                    <>
                        <hr className="mt-8 border-t border-gray-200 dark:border-gray-700" />
                        <div className="mt-8 flex justify-end">
                            <Button
                                onClick={props.onButtonClick}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2 rounded-lg"
                                style={{
                                    paddingRight: "24px",
                                    paddingLeft: "24px",
                                    borderRadius: "0.375rem",
                                }}
                            >
                                Submit
                            </Button>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}